import Box from "@cloudscape-design/components/box"
import Button from "@cloudscape-design/components/button"
import FileDropzone from "@cloudscape-design/components/file-dropzone"
import Icon from "@cloudscape-design/components/icon"
import SpaceBetween from "@cloudscape-design/components/space-between"
import React, { useRef, useState } from "react"

interface FileUploadZoneProps {
  onFileSelect: (file: File) => void
  acceptedFormats: string[]
  maxSizeMB: number
  disabled?: boolean
  requireZip?: boolean
}

interface ValidationError {
  type: string
  message: string
  suggestedFix?: string
}

const formatErrorLine = (error: ValidationError): string =>
  error.suggestedFix ? `${error.message} Suggestion: ${error.suggestedFix}` : error.message

// Wording for the persistent dashed drop target. Kept generic (no vendor-specific
// copy) so every caller of this shared component reads the same way.
const DROPZONE_TEXT = "Drag and drop your file here, or choose a file below."

// Escape hatch: Cloudscape FileDropzone/Button offer no combined "disabled dropzone"
// treatment, so we dim the whole control and block pointer interaction via a wrapper
// while an upload is in flight. `handleChange` is the authoritative guard; this style
// is the visual/pointer affordance. Isolated here to keep the one unavoidable inline
// style explicit rather than buried in JSX.
const DISABLED_WRAPPER_STYLE: React.CSSProperties = { opacity: 0.5, pointerEvents: "none" }

const FileUploadZone: React.FC<FileUploadZoneProps> = ({
  onFileSelect,
  acceptedFormats,
  maxSizeMB,
  disabled = false,
  requireZip = false,
}) => {
  // A locally-rejected file is kept so its validation error can be shown beneath the
  // drop target. A valid file is handed off via `onFileSelect` (the parent renders the
  // selection) and nothing is retained here.
  const [rejectedFile, setRejectedFile] = useState<File | null>(null)
  const [errors, setErrors] = useState<ValidationError[]>([])

  // Cloudscape FileDropzone has no built-in file picker, so we drive a hidden native
  // input from the "Choose file" button to keep click-to-browse working alongside drops.
  const fileInputRef = useRef<HTMLInputElement>(null)

  const validateFile = (file: File): ValidationError[] => {
    const validationErrors: ValidationError[] = []

    // File size validation
    const maxSizeBytes = maxSizeMB * 1024 * 1024
    if (file.size > maxSizeBytes) {
      validationErrors.push({
        type: "FILE_TOO_LARGE",
        message: `File size exceeds maximum of ${maxSizeMB}MB`,
        suggestedFix: "Split the file into smaller chunks or compress the data",
      })
    }

    // File format validation (skip if acceptedFormats is empty - means accept all)
    const fileExt = file.name.substring(file.name.lastIndexOf(".")).toLowerCase()

    if (acceptedFormats.length > 0) {
      const normalizedFormats = acceptedFormats.map((f) => f.toLowerCase())
      if (!normalizedFormats.includes(fileExt)) {
        validationErrors.push({
          type: "INVALID_FORMAT",
          message: `File format ${fileExt} not allowed. Expected: ${acceptedFormats.join(", ")}`,
          suggestedFix: `Convert file to one of: ${acceptedFormats.join(", ")}`,
        })
      }
    }

    // ZIP requirement validation
    if (requireZip && fileExt !== ".zip") {
      validationErrors.push({
        type: "ZIP_REQUIRED",
        message: "Files must be submitted as ZIP archives",
        suggestedFix: "Create a ZIP file containing your data files",
      })
    }

    return validationErrors
  }

  const handleChange = (files: File[]) => {
    // Ignore selection while disabled (e.g. an upload is in flight).
    if (disabled) return

    const file = files[0] ?? null
    if (!file) {
      setRejectedFile(null)
      setErrors([])
      return
    }

    const validationErrors = validateFile(file)
    if (validationErrors.length > 0) {
      setRejectedFile(file)
      setErrors(validationErrors)
      return
    }

    setRejectedFile(null)
    setErrors([])
    onFileSelect(file)
  }

  const openFilePicker = () => {
    if (disabled) return
    fileInputRef.current?.click()
  }

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleChange(Array.from(event.target.files ?? []))
    // Reset so selecting the same file again still fires a change event.
    event.target.value = ""
  }

  const constraintParts: string[] = []
  if (acceptedFormats.length > 0) {
    constraintParts.push(`Supports: ${acceptedFormats.join(", ")}.`)
  }
  constraintParts.push(`Max size: ${maxSizeMB}MB.`)
  if (requireZip) {
    constraintParts.push("ZIP file required for this data stream.")
  }

  // See DISABLED_WRAPPER_STYLE. The dashed FileDropzone is always mounted so the
  // drag-and-drop target is discoverable at rest (not only mid-drag). `handleChange`
  // guards the disabled case for both drops and the picker.
  return (
    <div
      aria-disabled={disabled || undefined}
      style={disabled ? DISABLED_WRAPPER_STYLE : undefined}
    >
      <SpaceBetween size="xs">
        <FileDropzone onChange={({ detail }) => handleChange(detail.value)}>
          <SpaceBetween size="xs" alignItems="center">
            <Icon name="upload" size="big" />
            <Box color="text-body-secondary" textAlign="center">
              {DROPZONE_TEXT}
            </Box>
            <Button
              iconName="folder-open"
              formAction="none"
              disabled={disabled}
              onClick={openFilePicker}
            >
              Choose file
            </Button>
          </SpaceBetween>
        </FileDropzone>

        <Box fontSize="body-s" color="text-body-secondary">
          {constraintParts.join(" ")}
        </Box>

        {rejectedFile && errors.length > 0 && (
          <Box fontSize="body-s" color="text-status-error">
            {rejectedFile.name}: {errors.map(formatErrorLine).join(" ")}
          </Box>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept={acceptedFormats.length > 0 ? acceptedFormats.join(",") : undefined}
          onChange={handleInputChange}
          style={{ display: "none" }}
          aria-hidden="true"
          tabIndex={-1}
        />
      </SpaceBetween>
    </div>
  )
}

export default FileUploadZone
